import { COST } from "components/Budgeting2023/options/margin/FormulasTab"
import { isNil, isNaN } from "lodash"

export const getMargin = partida => {
  if (partida.hasOwnProperty("margin") && !isNil(partida.margin)) {
    return parseFloat(partida.margin)
  } else {
    return (
      parseFloat(partida.costeGeneral) +
      parseFloat(partida.costeIndirecto) +
      parseFloat(partida.industrialProfit)
    )
  }
}

export const calcMarginFactor = partida => {
  return 1 - getMargin(partida) / 100
}

export const marginCalc = (partida, formula) => {
  //console.log(`marginCalc: price => ${partida.price}, clientPrice => ${partida.clientPrice}`)
  if (
    !isNaN(partida.price) &&
    !isNaN(partida.clientPrice) &&
    partida.clientPrice !== 0
  ) {
    return calcMargin(partida.price, partida.clientPrice, formula)
  } else {
    return partida.margin || 0
  }
}

// calcCostTotal -> getTotalPrice
export const calcCostTotal = partida => {
  var costTotal = parseFloat(partida.price) * parseFloat(partida.quantity)
  //console.log(`calcCostTotal: costTotal => ${costTotal}`)
  return costTotal
}

// calcApplyDiscount -> getClientPriceWithDiscount
export const calcApplyDiscount = (price, discount) => {
  return price * (1 - discount / 100)
}

export const calcClientPriceWithoutDiscount = (partida, formula) => {
  if (isNaN(partida.price) || partida.price === 0) {
    return 0
  } else {
    let clientPriceWithoutDiscount = 0
    if (formula === COST) {
      const margin = getMargin(partida)
      //part.price * (1 + margins/100) (editado)
      clientPriceWithoutDiscount = partida.price * (1 + margin / 100)
    } else {
      const margin = calcMarginFactor(partida, formula)
      clientPriceWithoutDiscount = margin
        ? parseFloat(partida.price) / calcMarginFactor(partida)
        : partida.price
    }

    return clientPriceWithoutDiscount
  }
}

export const calcClientPrice = (partida, formula) => {
  if (isNil(partida.discount) || partida.discount === 0) {
    return calcClientPriceWithoutDiscount(partida, formula)
  } else {
    return (
      calcClientPriceWithoutDiscount(partida, formula) *
      (1 - parseFloat(partida.discount) / 100)
    )
  }
}

// calcPriceTotal -> getTotalClientPrice
export const calcPriceTotal = (partida, formula) => {
  var priceTotal = partida.clientPrice * partida.quantity
  return priceTotal
}

export const costRealCalc = partida => {
  return !isNil(partida.costReal)
    ? parseFloat(partida.costReal)
    : parseFloat(partida.price)
}

const sumExpenseImputationsAllocated = expenseImputations =>
  expenseImputations.reduce(
    (sum, expense) => sum + (Number(expense?.allocated) || 0),
    0,
  )

export const costRealTotalCalc = partida => {
  const expenseImputations = partida.expenseImputations

  if (expenseImputations.length > 0) {
    return sumExpenseImputationsAllocated(expenseImputations)
  }

  const unitCost = costRealCalc(partida)
  const quantity = Number(partida.quantity)

  if (isNaN(unitCost) || isNaN(quantity)) {
    return 0
  }

  return unitCost * quantity
}

export const underObjective = partida => {
  return partida.costReal && partida.costReal < partida.price
}

export const overObjective = partida => {
  return partida.costReal && partida.costReal > partida.price
}

export const overPrice = partida => {
  return partida.costReal && partida.costReal > partida.clientPrice
}

export const calcPartida = (partida, updatedField, updatedValue, formula) => {
  partida[updatedField] = updatedValue

  switch (updatedField) {
    case "quantity":
    case "price":
    case "costReal":
    case "discount":
    case "margin":
      partida.clientPriceWithoutDiscount = calcClientPriceWithoutDiscount(
        partida,
        formula,
      )
      partida.clientPrice = calcClientPrice(partida, formula)
    case "clientPrice":
    case "clientPriceWithoutDiscount":
      partida.margin = marginCalc(partida, formula)
    default:
      break
  }

  return partida
}

export const calcularMargenRealPorcentaje = (partida, formula) => {
  const { price, priceReduced, costReal, clientPrice } = partida
  const priceValue = costReal ?? priceReduced ?? price
  if (!clientPrice) return 0

  const margin =
    formula !== COST
      ? 100 * (1 - priceValue / clientPrice)
      : ((clientPrice - priceValue) / priceValue) * 100

  return margin
}

// *********************

export const calcMarginWithCostFormula = (cost, price) => {
  const margin = price / cost - 1
  return margin * 100
}

export const calcMarginWithClientFormula = (cost, price) => {
  const margin = 1 - cost / price
  return margin * 100
}

export const calcMargin = (cost, price, formula) =>
  formula === COST
    ? calcMarginWithCostFormula(cost, price)
    : calcMarginWithClientFormula(cost, price)

// applyCostMargin -> getPriceWithCostMargin
export const applyCostMargin = (price, marginPercent) => {
  return price * (1 + marginPercent / 100)
}

// applyClientMargin -> getPriceWithClientMargin
export const applyClientMargin = (price, margin) => {
  const newMargin = margin / 100
  const divisor = 1 - newMargin
  const finalPrice = price / divisor

  return isFinite(finalPrice) ? finalPrice : 0
}

export function calculateTotalWithDiscount(price, quantity, discount) {
  if (!price || !quantity) return 0
  if (!discount || discount <= 0) return price * quantity
  const discountedPrice = price - (discount / 100) * price

  return discountedPrice * quantity
}

/**
 * Calculates the reduced price after applying a reduction percentage.
 * If the reduction is 0, null or undefined, it returns the original price as-is.
 * @param {number} price - The original price before reduction.
 * @param {number} costReduction - The reduction percentage (e.g., 10 for 10%).
 * @returns {number} - The reduced price.
 */
// applyCostReduction -> getPriceReduced
export const applyCostReduction = (price, costReduction) => {
  if (!costReduction || isNaN(costReduction)) {
    return price
  }

  const costReductionDecimal = costReduction / 100
  const priceReduced = price * (1 - costReductionDecimal)
  return priceReduced
}

/**
 * Builds the certification view state object from partida and cert_current.
 * @param {Object} partida - Partida with cert_previous, quantity
 * @param {number} certCurrent - Current certification value
 * @param {number} unitPrice - Price per unit for cert_currentPriceView (e.g. clientPrice or price)
 * @returns {Object} View state with cert_totalView, cert_currentView, cert_currentPriceView, cert_totalPercView, cert_currentPercView
 */
export const getCertViewStateFromPartida = (
  partida,
  certCurrent,
  unitPrice,
) => {
  const certPrev = partida.cert_previous ?? 0
  const total = certPrev + certCurrent
  const qty = partida.quantity || 1
  const price = unitPrice ?? 0
  return {
    cert_totalView: total.toFixed(2),
    cert_currentView: certCurrent.toFixed(2),
    cert_currentPriceView: (certCurrent * price).toFixed(2),
    cert_totalPercView: parseFloat((100 * total) / qty).toFixed(2),
    cert_currentPercView: parseFloat((100 * certCurrent) / qty).toFixed(2),
  }
}
